import { useNavigate } from 'react-router-dom';

function AnimatedText({ text, baseDelay, className }: { text: string; baseDelay: number; className?: string }) {
  return (
    <span className={className}>
      {text.split('').map((char, i) => (
        <span
          key={i}
          className="animate-word-reveal inline-block"
          style={{ animationDelay: `${baseDelay + i * 0.04}s` }}
        >
          {char}
        </span>
      ))}
    </span>
  );
}

export default function HeroSection() {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-[540px] md:min-h-[820px] flex items-center bg-white overflow-hidden">
      {/* Left content */}
      <div className="w-full md:w-[50%] px-5 md:px-10 lg:px-16 pt-20 md:pt-28 pb-8 md:pb-12 flex flex-col justify-center z-10">
        {/* 小標籤 */}
        <span className="inline-block text-xs font-semibold tracking-widest text-accent-600 uppercase mb-3 animate-fade-in-up delay-300">
          富聆聽健康 — 專業聽力照護
        </span>

        <h1 className="text-[1.75rem] leading-snug md:text-4xl lg:text-5xl md:leading-loose font-bold text-foreground-900">
          <span className="block mb-1 md:mb-2">
            <AnimatedText text="把耳朵交給我們，" baseDelay={0.0} />
          </span>
          <span className="block text-accent-600">
            <AnimatedText text="把時間留給對話" baseDelay={0.36} />
          </span>
        </h1>
        <p className="mt-3 md:mt-6 text-foreground-600 text-base md:text-lg leading-relaxed max-w-md animate-fade-in-up delay-600">
          富聆聽健康提供全方位聽力檢測與專業助聽器選配，讓每一位用戶都能享受清晰的聆聽體驗。
        </p>

        <div className="mt-5 md:mt-8 w-full max-w-md animate-fade-in-up delay-700">
          <button
            type="button"
            onClick={() => navigate('/hearing-test')}
            className="w-full group relative overflow-hidden rounded-xl bg-primary-800 text-white px-6 py-4 md:py-5 text-base font-medium hover:bg-primary-900 transition-all duration-300 flex items-center justify-center gap-3"
          >
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 group-hover:bg-white/20 transition-all duration-300">
              <i className="ri-headphone-line text-lg"></i>
            </span>
            <span className="whitespace-nowrap">線上聽力自我檢測</span>
            <i className="ri-arrow-right-line text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all duration-300"></i>
          </button>
          <p className="mt-2 text-xs text-foreground-500 text-center">
            3 分鐘快速完成，即時獲得聽力健康評估
          </p>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 animate-fade-in-up delay-800">
          <button
            type="button"
            onClick={() => navigate('/booking')}
            className="bg-accent-500 text-background-50 px-6 md:px-7 py-3 md:py-3.5 rounded-xl text-base font-medium hover:bg-accent-600 transition-all duration-200 whitespace-nowrap"
          >
            預約免費諮詢
          </button>
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById('knowledge-section');
              if (!el) return;
              const offset = 100;
              const target = el.getBoundingClientRect().top + window.scrollY - offset;
              const start = window.scrollY;
              const distance = target - start;
              const duration = 1200;
              let startTime: number | null = null;
              const animate = (currentTime: number) => {
                if (!startTime) startTime = currentTime;
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const ease = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
                window.scrollTo(0, start + distance * ease);
                if (progress < 1) requestAnimationFrame(animate);
              };
              requestAnimationFrame(animate);
            }}
            className="border border-background-200 text-foreground-600 px-6 md:px-7 py-3 md:py-3.5 rounded-xl text-base font-medium hover:border-accent-400 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-volume-up-line text-accent-600"></i>
            聽力大小事
          </button>
        </div>
      </div>

      {/* Right image */}
      <div className="hidden md:block md:w-[50%] absolute right-0 top-0 bottom-0 animate-fade-in delay-500">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20caring%20scene%20of%20elderly%20person%20being%20assisted%20with%20hearing%20aid%20by%20professional%20audiologist%20in%20modern%20bright%20clinic%20soft%20natural%20lighting%20green%20and%20orange%20color%20tones%20professional%20photography%20high%20quality&width=960&height=680&seq=30&orientation=landscape"
          alt="聽力檢測專業服務"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/40 to-transparent" />
      </div>
    </section>
  );
}